import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import { fetchPresignedFile } from '../../../utils/fileUtils';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

interface PdfViewerProps {
  previewFile: string;
  handleDocumentLoadSuccess: (doc: { numPages: number }) => void;
  zoom: number;
  currentPage: number;
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfViewer: React.FC<PdfViewerProps> = ({
  previewFile,
  handleDocumentLoadSuccess,
  zoom,
  currentPage,
}) => {
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);

  useEffect(() => {
    const fetchPdfAsDataUrl = async () => {
      try {
        const response = await fetchPresignedFile(previewFile);

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.statusText}`);
        }

        const blob = await response.blob();

        const reader = new FileReader();
        reader.onloadend = () => {
          setPdfDataUrl(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setPdfDataUrl(null);
      }
    };

    fetchPdfAsDataUrl();
  }, [previewFile]);

  const safePageNumber = useMemo(() => {
    if (!numPages) return Math.max(currentPage + 1, 1);
    return Math.min(Math.max(currentPage + 1, 1), numPages);
  }, [currentPage, numPages]);

  const safeZoom = useMemo(() => {
    return zoom > 0 ? zoom : 1;
  }, [zoom]);

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        height: '100%',
        overflowY: 'auto',
        width: '100%',
        backgroundColor: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 2,
      }}
    >
      {!pdfDataUrl ? null : (
        <Document
          file={pdfDataUrl}
          onLoadSuccess={(pdf) => {
            setNumPages(pdf.numPages);
            handleDocumentLoadSuccess({ numPages: pdf.numPages });
          }}
          onLoadError={(error) => {
            console.error('Error rendering PDF:', error);
          }}
          loading={null}
        >
          <Page
            pageNumber={safePageNumber}
            scale={safeZoom}
            renderAnnotationLayer
            renderTextLayer
          />
        </Document>
      )}
    </Box>
  );
};

export default PdfViewer;